import { FormProps, InputText, InputTextArea } from "@components-common/forms";
import { application } from "../../../_runtime/bootstrap";
import { useFormikContext } from "formik";
import { FC, useEffect, useState } from "react";
import { Col, Loader, Row } from "taltech-styleguide";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { HttpResponse } from "@dirootie-core/http-types";
import { TableGroupsSelection } from "./TableGroupsSelection";
import { useUserContext } from "../../../_runtime/iam/session.context";

type Props = {
  users: any[];
  selectedUsers: string[];
  setSelectedUsers: any;
};

const columns = [
  {
    header: "",
    accessor: "id",
  },
  {
    header: "Name",
    accessor: "displayName",
  },
  {
    header: "Role",
    accessor: "role",
  },
  {
    header: "Email",
    accessor: "email",
  },
];

const GroupFormContent: FC<Props> = ({
  users,
  selectedUsers,
  setSelectedUsers,
}) => (
  <>
    <div className="d-flex flex-column gap-2">
      <Col md="8" lg="8" xl="6">
        <InputText id="name" elementClass="required" />
      </Col>
      <Col md="8" lg="8" xl="6">
        <InputTextArea id="description" />
      </Col>
      <TableGroupsSelection
        tableItems={users}
        columns={columns}
        hasEdit={true}
        selected={selectedUsers}
        setSelected={setSelectedUsers}
      />
    </div>
  </>
);

export const GroupForm: FC<FormProps> = ({ ...props }) => {
  const { groupId, id } = useParams();
  const nav = useNavigate();
  const formik = useFormikContext();
  const {userId} = useUserContext();
  const [originalSelectedUsers, setOriginalSelectedUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState(props.id === "form_create" ? [userId as string] : []);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { data: users } = useQuery(
    `students&${searchTerm}`,
    () => {
      return application
        .getHttpClient()
        .get(`/coursesms/members/all?courseId=${id}&displayName=${searchTerm}`)
        .then((x: HttpResponse) => {
          return x.body.items || [];
        });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (props.id !== "form_create") {
      application
        .getHttpClient()
        .get(`/coursesms/groupuserprofiles?groupId=${groupId}`)
        .then((res: any) => {
          const users = res.body.items.map((x: any) => x.userProfileId);
          setOriginalSelectedUsers(res.body.items);
          setSelectedUsers(users);
        });
    }
  }, []);

  useEffect(() => {
    if (formik.isSubmitting) {
      const values: any = formik.values;
      values.courseId = id;

      const editedUsers = mapSelectedUsers(
        originalSelectedUsers,
        selectedUsers
      );

      groupFormSubmission(
        values,
        formik,
        nav,
        props.id === "form_edit",
        selectedUsers,
        editedUsers
      );
    }
  }, [formik.isSubmitting]);

  return users === undefined ? (
    <Loader overlay />
  ) : (
    <Row>
      <GroupFormContent
        users={users}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
      />
    </Row>
  );
};

const mapSelectedUsers = (
  originalSelectedUsers: any[],
  newListOfUsers: string[]
) => {
  const addedUsers = newListOfUsers.filter(
    (x) =>
      originalSelectedUsers.find(
        (userGroup: any) => userGroup.userProfileId === x
      ) === undefined
  );
  const removedUsers = originalSelectedUsers.filter(
    (x) => !newListOfUsers.includes(x.userProfileId)
  );

  return {
    addedUsers,
    removedUsers,
  };
};

const groupFormSubmission = (
  values: any,
  formik: any,
  nav: any,
  isEditForm: boolean,
  selectedUsers: string[],
  editedUsers: any
) => {
  const data = {
    name: values.name,
    description: values.description,
    courseId: values.courseId,
  };

  if (formik.isValid) {
    if (!isEditForm) {
      application
        .getHttpClient()
        .post("/coursesms/groups", data)
        .then((res: any) => {
          if (selectedUsers.length > 0) {
            selectedUsers.forEach((userId, index) => {
              createGroupUser(
                res.body.id,
                userId,
                index === selectedUsers.length - 1,
                nav
              );
            });
          }
          
          nav(-1);
        })
        .catch((e) => {
          formik.setSubmitting(false);
        });
    } else {
      application
        .getHttpClient()
        .put(`/coursesms/groups/${values.id}`, data)
        .then((res: any) => {
          if (editedUsers.addedUsers.length > 0) {
            editedUsers.addedUsers.forEach((userId: string, index: number) => {
              createGroupUser(
                res.body.id,
                userId,
                index === selectedUsers.length - 1,
                nav
              );
            });
          }
          if (editedUsers.removedUsers.length > 0) {
            editedUsers.removedUsers.forEach((groupUser: any) => {
              deleteGroupUser(groupUser.id);
            });
          }

          nav(-1);
        })
        .catch((e) => {
          formik.setSubmitting(false);
        });
    }
  }
};

const createGroupUser = (
  groupId: string,
  userId: string,
  isLast: boolean,
  nav: any
) => {
  const groupUser = {
    groupId,
    userProfileId: userId,
  };

  application
    .getHttpClient()
    .post("/coursesms/groupuserprofiles", groupUser)
    .catch((e) => console.log(e));
};

const deleteGroupUser = (id: string) =>
  application
    .getHttpClient()
    .delete(`/coursesms/groupuserprofiles/${id}`)
    .catch((e) => console.log(e));
